import React, { useEffect, useRef, useState } from 'react';
import * as VexFlowImport from 'vexflow';
import { midiToName, midiToNashvilleNumber, midiToRomanNumeral, getOvertoneNotes } from '../utils';
import '../App.css'; // Import to access Bravura Text font and chord/note CSS classes

// Type assertion for VexFlow to avoid TypeScript issues
const VF = VexFlowImport as any;

// Create namespace for type annotations
namespace VF {
  export type RenderContext = any;
  export type Stave = any;
  export type Voice = any;
  export type StaveNote = any;
  export type Formatter = any;
  export type Accidental = any;
}

type ChordSegment = {
  root_pc: number;
  third_quality: string;
  third_quality_wbn: string;
  extension: string;
  extension_wbn: string;
  bass_pc: number;
  weight: number;
  mode: number;
  chord_qual: number;
};

type NoteData = {
  midiNote: number;
  note: string;
  accidental?: string;
  octave: number;
};

type StaffViewProps = {
  midiNotes: number[];
  keyCenter?: number;
  mode?: number;
  width?: number | string;
  height?: number | string;
  lockedTonicNote?: number | null;
  lockedTonicMode?: number | null;
  rootNote?: number;
  isScale?: boolean;
  // Chord symbol props
  chordSymbols?: string;
  chordSymbol?: string;
  csOptionOneSegments?: ChordSegment;
  scaleRoot?: number;
  scaleUrl?: string;
  activePcs?: number[];
  activeNotes?: number[];
  heldNotes?: number[];
  toggledNotes?: number[];
  assembleChordSymbol?: (segment: ChordSegment, isSvg?: boolean, lockedTonicNote?: number | null, lockedTonicMode?: number | null) => JSX.Element;
  assembleScaleName?: (scaleRoot: number, chord_qual: number) => JSX.Element | undefined;
  // Flash props
  keyflash?: number[];
  // Roman numeral props
  showRomanNumerals?: boolean;
  showNashvilleNumber?: boolean;
  chordQual?: number;
  // Staff Color props
  preferWhiteStaffPaper?: boolean;
  showOvertones?: boolean;
  appTheme?: string;
};

type VoiceEntry = { voice: any; stave: any };

const StaffView: React.FC<StaffViewProps> = ({
  midiNotes,
  keyCenter = 0,
  mode = 0,
  width = '25.5dvw',
  height = '30.5dvw',
  lockedTonicNote = null,
  lockedTonicMode = null,
  rootNote = 0,
  isScale = false,
  // Chord symbol props
  chordSymbols = '',
  chordSymbol = '',
  csOptionOneSegments,
  scaleRoot = 0,
  scaleUrl = '',
  activePcs = [],
  activeNotes = [],
  heldNotes = [],
  toggledNotes = [],
  assembleChordSymbol,
  assembleScaleName,
  // Flash props
  keyflash = [],
  // Roman numeral props
  showRomanNumerals = true,
  showNashvilleNumber = false,
  chordQual = 0,
  // Staff Color props
  preferWhiteStaffPaper = false,
  showOvertones = false,
  appTheme = 'dark',
}) => {
  // Filter out invalid MIDI notes at the component level
  const validMidiNotes = midiNotes.filter(note => note >= 0 && note <= 127);

  const containerRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Listen for window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Convert dvw/dvh to pixels
  const convertToPixels = (value: number | string): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (value.includes('dvw')) {
        const dvwValue = parseFloat(value.replace('dvw', ''));
        return (dvwValue / 100) * windowSize.width;
        // } else if (value.includes('dvh')) {
        //   const dvhValue = parseFloat(value.replace('dvh', ''));
        //   return (dvhValue / 100) * windowSize.height;
      }
      return parseFloat(value) || 0;
    }
    return 0;
  };

  const pixelWidth = convertToPixels(width);
  const pixelHeight = convertToPixels(height);

  // Base dimensions for scaling reference
  const baseWidth = 290;
  const scale = pixelWidth / baseWidth;

  // Get accidentals in the key signature
  const getKeySignatureAccidentals = (keySignature: string): { [note: string]: string } => {
    const keyAccidentals: { [key: string]: { [note: string]: string } } = {
      'C': {},
      'G': { f: '#' },
      'D': { f: '#', c: '#' },
      'A': { f: '#', c: '#', g: '#' },
      'E': { f: '#', c: '#', g: '#', d: '#' },
      'B': { f: '#', c: '#', g: '#', d: '#', a: '#' },
      'F#': { f: '#', c: '#', g: '#', d: '#', a: '#', e: '#' },
      'C#': { f: '#', c: '#', g: '#', d: '#', a: '#', e: '#', b: '#' },
      'F': { b: 'b' },
      'Bb': { b: 'b', e: 'b' },
      'Eb': { b: 'b', e: 'b', a: 'b' },
      'Ab': { b: 'b', e: 'b', a: 'b', d: 'b' },
      'Db': { b: 'b', e: 'b', a: 'b', d: 'b', g: 'b' },
      'Gb': { b: 'b', e: 'b', a: 'b', d: 'b', g: 'b', c: 'b' },
      'Cb': { b: 'b', e: 'b', a: 'b', d: 'b', g: 'b', c: 'b', f: 'b' }
    };
    return keyAccidentals[keySignature] || {};
  };

  // Get key signature based on lockedTonicNote and lockedTonicMode
  const getKeySignature = (tonicNote: number | null, tonicMode: number | null): string => {
    if (tonicNote === null || tonicMode === null) return 'C'; // Default to C major

    const pitchClass = tonicNote % 12;
    const isMajor = tonicMode === 0;

    if (isMajor) {
      // Major keys - direct mapping
      const majorKeys = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
      return majorKeys[pitchClass];
    } else {
      // Minor keys - use relative major key signatures, except Ab Minor = Cb
      if (pitchClass === 8) { // Ab Minor
        return 'Cb';
      } else {
        // Use relative major (3 semitones up)
        const relativeMajorPc = (pitchClass + 3) % 12;
        const majorKeys = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
        return majorKeys[relativeMajorPc];
      }
    }
  };

  // Convert MIDI note number to VexFlow note format using existing midiToName utility
  const midiToVexNote = (midiNote: number): { note: string; octave: number; accidental?: string } => {
    // Use locked tonic note and mode if set, otherwise fall back to keyCenter and mode
    const effectiveKeyCenter = lockedTonicNote !== null ? lockedTonicNote : keyCenter;
    const effectiveMode = lockedTonicNote !== null ? lockedTonicMode ? lockedTonicMode : 0 : chordQual;
    const noteName = midiToName(midiNote, false, effectiveKeyCenter, effectiveMode);
    const octave = Math.floor(midiNote / 12) - 1;

    // Handle accidentals
    let cleanNote = noteName;
    let accidental: string | undefined;

    if (noteName.includes('♯')) {
      cleanNote = noteName.replace('♯', '');
      accidental = '#';
    } else if (noteName.includes('♭')) {
      cleanNote = noteName.replace('♭', '');
      accidental = 'b';
    }

    return {
      note: cleanNote,
      octave,
      accidental
    };
  };

  // Determine if note should go on treble or bass clef
  const shouldUseTrebleClef = (midiNote: number): boolean => {
    return midiNote >= 60; // Middle C (C4) and above go to treble clef
  };

  // Filter scale notes to one octave of adjacent notes
  const filterScaleToOneOctave = (midiNotes: number[]): number[] => {
    if (midiNotes.length === 0) return [];

    // Sort notes by pitch
    const sortedNotes = [...midiNotes].sort((a, b) => a - b);

    // Find the lowest note and normalize it to middle C range
    const lowestNote = sortedNotes[0];
    const lowestPitchClass = lowestNote % 12;

    // Calculate how many octaves to shift to get into middle C range
    const octaveShift = Math.floor((60 - lowestNote) / 12);
    const normalizedLowest = lowestNote + (octaveShift * 12);
    const startNote = normalizedLowest < 60 ? normalizedLowest + 12 : normalizedLowest;
    const octaveAdjustment = startNote - lowestNote;

    // Create a set to track which pitch classes we've included
    const includedPitchClasses = new Set<number>();
    const oneOctaveNotes: number[] = [];

    // Process notes from lowest to highest, applying the octave adjustment
    for (const note of sortedNotes) {
      const pitchClass = note % 12;
      if (!includedPitchClasses.has(pitchClass)) {
        includedPitchClasses.add(pitchClass);
        oneOctaveNotes.push(note + octaveAdjustment);
      }
    }

    // Sort the final notes to ensure they're in ascending order
    return oneOctaveNotes.sort((a, b) => a - b);
  };

  // Resolve letter name collisions by respelling accidental notes
  const resolveLetterCollisions = (noteDataArray: Array<{ midiNote: number, note: string, octave: number, accidental?: string }>, keySignature: string, rootNote: number, mode: number) => {
    let result = [...noteDataArray];
    let hasChanges = true;
    let iterations = 0;
    const maxIterations = 5; // Prevent infinite loops

    while (hasChanges && iterations < maxIterations) {
      hasChanges = false;
      iterations++;

      // Group notes by letter name
      const letterGroups: { [letter: string]: number[] } = {};
      result.forEach((noteData, index) => {
        const letter = noteData.note.toLowerCase();
        if (!letterGroups[letter]) letterGroups[letter] = [];
        letterGroups[letter].push(index);
      });

      // Find collisions (same letter with different accidentals, only if they're close enough to visually collide)
      for (const [letter, indices] of Object.entries(letterGroups)) {
        if (indices.length > 1) {
          // Find pairs of notes that are actually colliding (same octave)
          const collidingPairs: Array<[number, number]> = [];
          for (let i = 0; i < indices.length; i++) {
            for (let j = i + 1; j < indices.length; j++) {
              const noteA = result[indices[i]];
              const noteB = result[indices[j]];
              // Only consider it a collision if notes are in same octave
              const octaveDiff = Math.abs(noteA.octave - noteB.octave);
              if (octaveDiff === 0) {
                collidingPairs.push([indices[i], indices[j]]);
              }
            }
          }

          if (collidingPairs.length === 0) continue; // No actual collisions

          // Resolve each colliding pair separately
          for (const [indexA, indexB] of collidingPairs) {
            const noteA = result[indexA];
            const noteB = result[indexB];


            // Determine which is "natural" and which is "accidental" for decision-making
            let naturalNote, accidentalNote, naturalIndex, accidentalIndex;

            if (!noteA.accidental && noteB.accidental) {
              naturalNote = noteA;
              accidentalNote = noteB;
              naturalIndex = indexA;
              accidentalIndex = indexB;
            } else if (noteA.accidental && !noteB.accidental) {
              naturalNote = noteB;
              accidentalNote = noteA;
              naturalIndex = indexB;
              accidentalIndex = indexA;
            } else {
              // Both have accidentals or both are natural - treat first as "natural" for decision
              naturalNote = noteA;
              accidentalNote = noteB;
              naturalIndex = indexA;
              accidentalIndex = indexB;
            }

            // Check which note would be easier to read in this key signature or chord context
            const noteToRespell = chooseNoteToRespell(naturalNote, accidentalNote, keySignature, rootNote, chordQual);

            // Get chord context for respelling when no key signature is locked
            const chordRootSpelling = lockedTonicNote === null ? midiToName(rootNote, false, rootNote, chordQual) : undefined;

            const noteToChange = noteToRespell === 'natural' ? naturalNote : accidentalNote;
            const indexToChange = noteToRespell === 'natural' ? naturalIndex : accidentalIndex;
            const respelled = respellNote(noteToChange.midiNote, noteToChange.note, noteToChange.accidental, keySignature, chordRootSpelling);

            // Don't trade a letter collision for a double accidental. In a flat key the only
            // flat-side alternative is often a double flat (A♮ -> B♭♭, which then pushes
            // B♭ -> C♭♭), and A♭ followed by A♮ with a courtesy natural reads better than
            // either. Leave the collision alone unless the respelling is no more complex.
            const respelledComplexity = respelled ? getAccidentalComplexity(respelled.accidental) : 0;
            const createsDoubleAccidental = respelledComplexity >= 2 &&
              respelledComplexity > getAccidentalComplexity(noteToChange.accidental);

            if (respelled && !createsDoubleAccidental) {
              result[indexToChange] = {
                midiNote: noteToChange.midiNote,
                ...respelled
              };
              hasChanges = true;
            }
          }
        }
      }
    }

    return result;
  };

  // Helper function to detect key signature type (consolidate logic)
  const getKeySignatureType = (keySignature: string): 'flat' | 'sharp' | 'natural' => {
    const flatKeys = ['Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb', 'Fb'];
    const sharpKeys = ['F#', 'C#', 'G#', 'D#', 'A#', 'E#', 'B#'];

    if (flatKeys.includes(keySignature)) return 'flat';
    if (sharpKeys.includes(keySignature)) return 'sharp';
    return 'natural';
  };

  // Choose which note to respell based on key signature or chord context
  const chooseNoteToRespell = (
    naturalNote: { note: string, accidental?: string },
    accidentalNote: { note: string, accidental?: string },
    keySignature: string,
    rootNote: number,
    // mode: number
    chordQual: number
  ): 'natural' | 'accidental' => {

    const naturalSpelling = naturalNote.note + (naturalNote.accidental || '');
    const accidentalSpelling = accidentalNote.note + (accidentalNote.accidental || '');
    const naturalLetter = naturalNote.note.toLowerCase();
    const accidentalLetter = accidentalNote.note.toLowerCase();

    // Global priority 1: ensure the chord's MAJOR 3rd is spelled correctly (relative to chord root)
    const majorThirdPitchClass = (rootNote + 4) % 12;
    const majorThirdSpelling = midiToName(majorThirdPitchClass, false, rootNote, chordQual);
    const majorThirdLetter = majorThirdSpelling.replace(/[♯♭#b]/, '').toLowerCase();

    if (naturalLetter === majorThirdLetter || accidentalLetter === majorThirdLetter) {
      const thirdSpellingFormatted = majorThirdSpelling.replace('♯', '#').replace('♭', 'b');
      if (naturalSpelling.toLowerCase() === thirdSpellingFormatted.toLowerCase()) {
        return 'accidental'; // keep natural as the chord's 3rd
      } else if (accidentalSpelling.toLowerCase() === thirdSpellingFormatted.toLowerCase()) {
        return 'natural';    // keep accidental as the chord's 3rd
      }
    }

    // Global priority 2: ensure the chord's PERFECT 4th is spelled correctly (relative to chord root)
    const perfectFourthPc = (rootNote + 5) % 12;
    const perfectFourthSpelling = midiToName(perfectFourthPc, false, rootNote, chordQual);
    const perfectFourthLetter = perfectFourthSpelling.replace(/[♯♭#b]/, '').toLowerCase();

    if (naturalLetter === perfectFourthLetter || accidentalLetter === perfectFourthLetter) {
      const fourthSpellingFormatted = perfectFourthSpelling.replace('♯', '#').replace('♭', 'b');
      if (naturalSpelling.toLowerCase() === fourthSpellingFormatted.toLowerCase()) {
        return 'accidental'; // keep natural as the chord's 4th
      } else if (accidentalSpelling.toLowerCase() === fourthSpellingFormatted.toLowerCase()) {
        return 'natural';    // keep accidental as the chord's 4th
      }
    }

    if (lockedTonicNote !== null) {
      // When locked to a key signature, prioritize key signature context over chord context
      const keyType = getKeySignatureType(keySignature);

      if (keyType === 'flat') {
        // In flat keys, prefer to keep flat-based spellings and respell natural notes to flat equivalents
        if (!naturalNote.accidental && (accidentalNote.accidental === 'b' || accidentalNote.accidental === 'bb')) {
          return 'natural'; // Respell the natural note to maintain flat consistency
        }
        // If both have accidentals, prefer the one with flats
        if (naturalNote.accidental && accidentalNote.accidental) {
          const naturalIsFlat = naturalNote.accidental.includes('b');
          const accidentalIsFlat = accidentalNote.accidental.includes('b');
          if (!naturalIsFlat && accidentalIsFlat) return 'natural'; // Respell the non-flat note
          if (naturalIsFlat && !accidentalIsFlat) return 'accidental'; // Respell the non-flat note
        }
      } else if (keyType === 'sharp') {
        // In sharp keys, prefer to keep sharp-based spellings
        if (!naturalNote.accidental && (accidentalNote.accidental === '#' || accidentalNote.accidental === '##')) {
          return 'natural'; // Respell the natural note to maintain sharp consistency
        }
        // If both have accidentals, prefer the one with sharps
        if (naturalNote.accidental && accidentalNote.accidental) {
          const naturalIsSharp = naturalNote.accidental.includes('#');
          const accidentalIsSharp = accidentalNote.accidental.includes('#');
          if (!naturalIsSharp && accidentalIsSharp) return 'natural'; // Respell the non-sharp note
          if (naturalIsSharp && !accidentalIsSharp) return 'accidental'; // Respell the non-sharp note
        }
      }
    } else {
      // When not locked, prioritize the chord context using rootNote + mode
      // Get the chord root's proper spelling using midiToName
      const chordRootSpelling = midiToName(rootNote, false, keyCenter, chordQual);

      // First priority: Check for chord root letter priority (when root is involved in collision)
      const rootSpellingFormatted = chordRootSpelling.replace('♯', '#').replace('♭', 'b');
      const naturalSpelling = naturalNote.note + (naturalNote.accidental || '');
      const accidentalSpelling = accidentalNote.note + (accidentalNote.accidental || '');

      // Only apply root priority if one of the colliding notes IS the root note
      if (naturalSpelling.toLowerCase() === rootSpellingFormatted.toLowerCase()) {
        return 'accidental'; // Natural note IS the chord root, respell accidental
      } else if (accidentalSpelling.toLowerCase() === rootSpellingFormatted.toLowerCase()) {
        return 'natural'; // Accidental note IS the chord root, respell natural
      }

      // Second priority (Maj3rd and Perf 4th) now handled globally above

      // Third priority: Determine chord context and prefer consistent accidentals
      const chordType = chordRootSpelling.includes('♭') || chordRootSpelling.includes('b') ? 'flat' :
        chordRootSpelling.includes('♯') || chordRootSpelling.includes('#') ? 'sharp' : 'natural';

      // Apply the same flat/sharp preference logic as key signatures
      if (chordType === 'flat') {
        // In flat chord contexts, prefer to keep flat-based spellings and respell natural notes to flat equivalents
        if (!naturalNote.accidental && (accidentalNote.accidental === 'b' || accidentalNote.accidental === 'bb')) {
          return 'natural'; // Respell the natural note to maintain flat consistency
        }
        // If both have accidentals, prefer the one with flats
        if (naturalNote.accidental && accidentalNote.accidental) {
          const naturalIsFlat = naturalNote.accidental.includes('b');
          const accidentalIsFlat = accidentalNote.accidental.includes('b');
          if (!naturalIsFlat && accidentalIsFlat) {
            return 'natural'; // Respell the non-flat note
          } else if (naturalIsFlat && !accidentalIsFlat) {
            return 'accidental'; // Respell the non-flat note
          }
        }
        // Legacy flat logic for single accidentals
        if (accidentalNote.accidental === 'b' && !naturalNote.accidental) {
          return 'natural'; // Keep the flat note, respell the natural
        } else if (naturalNote.accidental === 'b' && accidentalNote.accidental !== 'b') {
          return 'accidental'; // Keep the flat note, respell the other
        }
      } else if (chordType === 'sharp') {
        // In sharp chord contexts, prefer to keep sharp-based spellings and respell natural notes to sharp equivalents
        if (!naturalNote.accidental && (accidentalNote.accidental === '#' || accidentalNote.accidental === '##')) {
          return 'natural'; // Respell the natural note to maintain sharp consistency
        }
        // If both have accidentals, prefer the one with sharps
        if (naturalNote.accidental && accidentalNote.accidental) {
          const naturalIsSharp = naturalNote.accidental.includes('#');
          const accidentalIsSharp = accidentalNote.accidental.includes('#');
          if (!naturalIsSharp && accidentalIsSharp) {
            return 'natural'; // Respell the non-sharp note
          } else if (naturalIsSharp && !accidentalIsSharp) {
            return 'accidental'; // Respell the non-sharp note
          }
        }
        // Legacy sharp logic for single accidentals
        if (accidentalNote.accidental === '#' && !naturalNote.accidental) {
          return 'natural'; // Keep the sharp note, respell the natural
        } else if (naturalNote.accidental === '#' && accidentalNote.accidental !== '#') {
          return 'accidental'; // Keep the sharp note, respell the other
        }
      } else {
        // For natural root chords (like C Major), prefer sharps over flats for black notes
        if (accidentalNote.accidental === '#' && naturalNote.accidental === 'b') {
          return 'natural'; // Keep sharp, respell flat to sharp
        } else if (naturalNote.accidental === '#' && accidentalNote.accidental === 'b') {
          return 'accidental'; // Keep sharp, respell flat to sharp  
        }
      }

      // Fourth priority: Prefer common enharmonic spellings
      // Special cases for common enharmonic preferences

      // C vs C# → prefer C#, respell C to B#
      if ((naturalSpelling === 'C' && accidentalSpelling === 'C#') ||
        (naturalSpelling === 'C#' && accidentalSpelling === 'C')) {
        return naturalSpelling === 'C' ? 'natural' : 'accidental';
      }

      // F vs F# → prefer F#, respell F to E#  
      if ((naturalSpelling === 'F' && accidentalSpelling === 'F#') ||
        (naturalSpelling === 'F#' && accidentalSpelling === 'F')) {
        return naturalSpelling === 'F' ? 'natural' : 'accidental';
      }
    }

    // Default: respell the accidental note (preserves original behavior for most cases)
    return 'accidental';
  };

  // Helper function to determine accidental complexity (for visual clutter preference)
  const getAccidentalComplexity = (accidental?: string): number => {
    if (!accidental) return 0;        // Natural = simplest
    if (accidental === '#') return 1;  // Sharp
    if (accidental === 'b') return 1;  // Flat  
    if (accidental === '##') return 2; // Double sharp
    if (accidental === 'bb') return 2; // Double flat
    return 0;
  };

  // Respell a note to avoid letter collision
  const respellNote = (midiNote: number, currentNote: string, currentAccidental?: string, keySignature?: string, chordRootSpelling?: string): { note: string, octave: number, accidental?: string } | null => {
    let octave = Math.floor(midiNote / 12) - 1;
    const pitchClass = midiNote % 12;

    // All enharmonic equivalents for each pitch class including double accidentals
    const enharmonics: { [pc: number]: string[] } = {
      0: ['C', 'B#', 'Dbb'],           // C <-> B# <-> Dbb
      1: ['C#', 'Db', 'B##'],          // C# <-> Db <-> B##
      2: ['D', 'C##', 'Ebb'],          // D <-> C## <-> Ebb
      3: ['D#', 'Eb', 'Fbb'],          // D# <-> Eb <-> Fbb
      4: ['E', 'Fb', 'D##'],           // E <-> Fb <-> D##
      5: ['F', 'E#', 'Gbb'],           // F <-> E# <-> Gbb
      6: ['F#', 'Gb', 'E##'],          // F# <-> Gb <-> E##
      7: ['G', 'F##', 'Abb'],          // G <-> F## <-> Abb
      8: ['G#', 'Ab'],                 // G# <-> Ab
      9: ['A', 'G##', 'Bbb'],          // A <-> G## <-> Bbb
      10: ['A#', 'Bb', 'Cbb'],         // A# <-> Bb <-> Cbb
      11: ['B', 'Cb', 'A##']           // B <-> Cb <-> A##
    };

    const alternatives = enharmonics[pitchClass];
    if (!alternatives || alternatives.length < 2) return null;

    // Find the best alternative spelling based on key signature context
    const currentSpelling = currentNote + (currentAccidental || '');
    const possibleAlternatives = alternatives.filter(alt => alt !== currentSpelling);

    if (possibleAlternatives.length === 0) return null;

    let alternative: string;

    if (possibleAlternatives.length === 1) {
      alternative = possibleAlternatives[0];
    } else {
      // Multiple alternatives available - choose based on key signature or chord context
      let contextType: 'flat' | 'sharp' | 'natural';

      if (keySignature && keySignature !== 'C') {
        // Use key signature context when available
        contextType = getKeySignatureType(keySignature);
      } else if (chordRootSpelling) {
        // Use chord context when no key signature is set
        contextType = chordRootSpelling.includes('♭') || chordRootSpelling.includes('b') ? 'flat' :
          chordRootSpelling.includes('♯') || chordRootSpelling.includes('#') ? 'sharp' : 'natural';
      } else {
        // Fallback to natural context
        contextType = 'natural';
      }

      if (contextType === 'flat') {
        // In flat keys, prefer flat-based spellings over sharp-based ones
        alternative = possibleAlternatives.find(alt => alt.includes('bb') || alt.includes('b')) ||
          possibleAlternatives.find(alt => !alt.includes('#')) ||
          possibleAlternatives[0];
      } else if (contextType === 'sharp') {
        // In sharp keys, prefer sharp-based spellings over flat-based ones  
        alternative = possibleAlternatives.find(alt => alt.includes('##') || alt.includes('#')) ||
          possibleAlternatives.find(alt => !alt.includes('b')) ||
          possibleAlternatives[0];
      } else {
        // No clear key context, use first alternative
        alternative = possibleAlternatives[0];
      }
    }

    if (alternative) {
      let cleanNote = alternative.replace(/[#b]/g, '');
      let accidental: string | undefined;

      // Handle double accidentals
      if (alternative.includes('##')) {
        accidental = '##';
      } else if (alternative.includes('bb')) {
        accidental = 'bb';
      } else if (alternative.includes('#')) {
        accidental = '#';
      } else if (alternative.includes('b')) {
        accidental = 'b';
      }

      // Handle octave adjustments for enharmonic equivalents that cross octave boundaries
      if (alternative === 'B#' && pitchClass === 0) { // B# for C
        octave -= 1; // B# is in the previous octave
      } else if (alternative === 'Cb' && pitchClass === 11) { // Cb for B
        octave += 1; // Cb is in the next octave
      } else if (alternative === 'Cbb' && pitchClass === 10) { // Cbb for Bb
        octave += 1; // C letter belongs to next octave up
      } else if (alternative === 'B##' && pitchClass === 1) { // B## for C#
        octave -= 1; // B letter belongs to previous octave
      }

      return {
        note: cleanNote,
        octave,
        accidental
      };
    }

    return null;
  };

  // // Resolve complex note clusters (3+ notes close together) NOT NEEDED! Not logical to shift clusters over at all.
  // const resolveComplexClusters = (chordNote: any, noteData: Array<{ midiNote: number, note: string, octave: number, accidental?: string }>) => {
  //   // Calculate staff positions for each note
  //   const staffPositions = noteData.map((data, index) => ({
  //     index,
  //     staffPosition: getStaffPosition(data.note, data.octave, chordNote.clef),
  //     midiNote: data.midiNote
  //   }));

  //   // Sort by staff position
  //   staffPositions.sort((a, b) => a.staffPosition - b.staffPosition);

  //   // Find clusters (notes within 1 staff position of each other)
  //   const clusters: number[][] = [];
  //   let currentCluster: number[] = [0];

  //   for (let i = 1; i < staffPositions.length; i++) {
  //     const prevPos = staffPositions[i - 1].staffPosition;
  //     const currentPos = staffPositions[i].staffPosition;

  //     if (Math.abs(currentPos - prevPos) <= 1) {
  //       // Notes are close, add to current cluster
  //       currentCluster.push(i);
  //     } else {
  //       // Gap found, start new cluster
  //       if (currentCluster.length > 2) {
  //         clusters.push([...currentCluster]);
  //       }
  //       currentCluster = [i];
  //     }
  //   }

  //   // Don't forget the last cluster
  //   if (currentCluster.length > 2) {
  //     clusters.push(currentCluster);
  //   }

  //   // Apply additional spacing to clusters with 3+ notes
  //   clusters.forEach(cluster => {
  //     cluster.forEach((staffPosIndex, clusterIndex) => {
  //       const noteIndex = staffPositions[staffPosIndex].index;

  //       if (clusterIndex >= 2) {
  //         // For 3rd+ notes in cluster, add extra X-shift
  //         const extraShift = (clusterIndex - 1) * 15; // 15px per extra position
  //         chordNote.setXShift(noteIndex, extraShift);
  //       }
  //     });
  //   });
  // };

  // Calculate staff position for a note (higher number = higher on staff)
  const getStaffPosition = (noteLetter: string, octave: number, clef: string): number => {
    const noteValues: { [key: string]: number } = {
      'c': 0, 'd': 1, 'e': 2, 'f': 3, 'g': 4, 'a': 5, 'b': 6
    };

    const basePosition = noteValues[noteLetter.toLowerCase()] || 0;
    const octaveOffset = clef === 'treble' ? octave * 7 : (octave + 1) * 7;

    return basePosition + octaveOffset;
  };

  // Create VexFlow note objects
  // overtoneNotes: when provided, combines with primary notes for collision detection and styles them grey
  const createVexFlowNotes = (notes: number[], clef: 'treble' | 'bass', keySignature: string, rootNote: number, mode: number, isScale: boolean = false, isOvertone: boolean = false, overtoneNotes: number[] = []) => {

    // Helper: process MIDI notes through filtering and spelling pipeline
    const processNotesToResolved = (midiNotes: number[], forScale: boolean) => {
      const oneOctaveNotes = forScale ? filterScaleToOneOctave(midiNotes) : midiNotes;
      const notesWithBass = forScale ?
        [...oneOctaveNotes, ...oneOctaveNotes.map(note => note - 24)] :
        oneOctaveNotes;
      const filteredNotes = notesWithBass.filter(note =>
        clef === 'treble' ? shouldUseTrebleClef(note) : !shouldUseTrebleClef(note)
      );
      if (filteredNotes.length === 0) return [];

      filteredNotes.sort((a, b) => a - b);
      const initialNoteData = filteredNotes.map(midiNote => ({
        midiNote,
        ...midiToVexNote(midiNote)
      }));
      return resolveLetterCollisions(initialNoteData, keySignature, rootNote, mode);
    };

    // Process primary and overtones SEPARATELY to preserve octave-specific spelling logic
    let resolvedNoteData: NoteData[];
    if (!isScale && overtoneNotes.length > 0) {
      const primaryResolved = processNotesToResolved(notes, false);
      const overtoneResolved = processNotesToResolved(overtoneNotes, false);
      resolvedNoteData = [...primaryResolved, ...overtoneResolved].sort((a, b) => a.midiNote - b.midiNote);
      if (resolvedNoteData.length === 0) return [];
    } else {
      resolvedNoteData = processNotesToResolved(notes, isScale);
      if (resolvedNoteData.length === 0) return [];
    }

    // resolvedNoteData is now ready (processed above)

    if (isScale) {
      // For scales, create individual notes arranged horizontally
      return resolvedNoteData.map(noteData => {
        const key = `${noteData.note.toLowerCase()}/${noteData.octave}`;
        const scaleNote = new VF.StaveNote({
          keys: [key],
          duration: 'q',
          clef
        })
          // .setStyle({ fillStyle: 'black' })  // make notehead solid black for light theme
          .setStyle({ fillStyle: preferWhiteStaffPaper ? 'rgb(0, 0, 0' : 'rgb(210, 210, 210)' })  // grey for overtones, normal colors for main notes
        // .setStemStyle({ hide: true });     // hide the stem
        scaleNote.setStem(new VF.Stem({ hide: true }))
        scaleNote.setLedgerLineStyle({
          strokeStyle: preferWhiteStaffPaper ? 'rgb(44, 44, 44)' : 'rgb(195, 195, 195)' // grey for overtones, normal colors for main notes
        })

        // Add accidentals for scale notes
        const keyAccidentals = getKeySignatureAccidentals(keySignature);
        const noteBaseName = noteData.note.toLowerCase();
        const keySignatureAccidental = keyAccidentals[noteBaseName];

        if (noteData.accidental !== keySignatureAccidental) {
          let displayAccidental = noteData.accidental || 'n';

          // Use Unicode symbols for double accidentals
          if (noteData.accidental === '##') {
            displayAccidental = '𝄪'; // Unicode double sharp
          } else if (noteData.accidental === 'bb') {
            displayAccidental = 'bb'; // Keep VexFlow's double flat
          }

          scaleNote.addModifier(new VF.Accidental(displayAccidental), 0);
        }

        // Create a voice for this single note
        // const voice = new VF.Voice({ num_beats: 1, beat_value: 4 });
        // voice.setMode(VF.Voice.Mode.SOFT);
        // voice.addTickable(scaleNote);

        // return voice;

        return scaleNote;
      });
    } else {
      // For chords, create a single chord with all notes stacked
      const keys = resolvedNoteData.map((noteData: NoteData) => {
        return `${noteData.note.toLowerCase()}/${noteData.octave}`;
      });

      const chordNote = new VF.StaveNote({
        keys: keys,
        duration: 'w',
        clef
      });

      // Set individual notehead colors when combining primary notes with overtones
      if (overtoneNotes.length > 0) {
        resolvedNoteData.forEach((noteData: NoteData, index: number) => {
          const isOvertoneNote = overtoneNotes.includes(noteData.midiNote);
          if (isOvertoneNote) {
            // Overtone notes styled grey and slightly smaller
            chordNote.setKeyStyle(index, {
              fillStyle: 'rgb(128, 128, 128)',
              // strokeStyle: 'rgb(128, 128, 128)', not used
            });
          } else {
            // Primary notes styled based on theme
            chordNote.setKeyStyle(index, {
              fillStyle: preferWhiteStaffPaper ? 'rgb(0, 0, 0)' : 'rgb(210, 210, 210)',
              strokeStyle: preferWhiteStaffPaper ? 'rgb(0, 0, 0)' : 'rgb(210, 210, 210)'
            });
          }
        });
      } else if (isOvertone) {
        // Legacy: all overtones in separate chord
        chordNote.setStyle({ fillStyle: 'rgb(128, 128, 128)' });
      }

      // Add accidentals that differ from the key signature + courtesy naturals for same-octave letter collisions
      const keyAccidentals = getKeySignatureAccidentals(keySignature);

      // Check for same-octave letter collisions that need courtesy naturals
      const letterOctaveGroups: { [key: string]: number[] } = {};
      resolvedNoteData.forEach((noteData, index) => {
        const key = `${noteData.note.toLowerCase()}_${noteData.octave}`;
        if (!letterOctaveGroups[key]) letterOctaveGroups[key] = [];
        letterOctaveGroups[key].push(index);
      });

      resolvedNoteData.forEach((noteData, index) => {
        const { note, accidental, octave } = noteData;
        const noteBaseName = note.toLowerCase();
        const keySignatureAccidental = keyAccidentals[noteBaseName];
        const octaveKey = `${noteBaseName}_${octave}`;

        let needsAccidental = accidental !== keySignatureAccidental;

        // Check if this note needs a courtesy natural due to same-octave letter collision
        if (!accidental && letterOctaveGroups[octaveKey] && letterOctaveGroups[octaveKey].length > 1) {
          // This is a natural note with same-letter notes in same octave - check if any have accidentals
          const hasAccidentalSibling = letterOctaveGroups[octaveKey].some(siblingIndex =>
            resolvedNoteData[siblingIndex].accidental
          );
          if (hasAccidentalSibling) {
            needsAccidental = true; // Force natural sign for clarity
          }
        }

        if (needsAccidental) {
          let displayAccidental = accidental || 'n'; // Use natural if no accidental needed

          // Use Unicode symbols for double accidentals instead of VexFlow's 'x'
          if (accidental === '##') {
            displayAccidental = '𝄪'; // Unicode double sharp
          } else if (accidental === 'bb') {
            displayAccidental = 'bb'; // Keep VexFlow's double flat
          }

          chordNote.addModifier(new VF.Accidental(displayAccidental), index);
        }
      });

      // Set ledger line color
      chordNote.setLedgerLineStyle({
        // fillStyle: '#ccc', 
        // strokeStyle: '#ccc' // #444 for black on white background, default so this line not needed
        strokeStyle: showOvertones ? 'rgb(128, 128, 128)' : (preferWhiteStaffPaper ? 'rgb(44, 44, 44)' : 'rgb(195, 195, 195)') // grey for overtones, normal colors for main notes
      });

      // After creating the chord, check for complex clusters and resolve them. Not needed. Not logical to shift clusters over at all.
      // resolveComplexClusters(chordNote, resolvedNoteData);

      return [chordNote];
    }
  };

  // Detect collision by checking for adjacent letter names (including cross-octave)
  const detectCollisions = (notes: number[]): number => {
    const allNotes = notes.map(midiNote => {
      const noteData = midiToVexNote(midiNote);
      return { letter: noteData.note.toLowerCase(), octave: noteData.octave };
    });

    let collisionCount = 0;
    // Check each note against every other note
    for (let i = 0; i < allNotes.length; i++) {
      for (let j = i + 1; j < allNotes.length; j++) {
        const note1 = allNotes[i];
        const note2 = allNotes[j];

        // Only check collisions for notes in the same or adjacent octaves
        const octaveDiff = Math.abs(note2.octave - note1.octave);
        if (octaveDiff <= 1) {
          // Check if notes are adjacent letters (excluding B-C same-octave case)
          const diff = note2.letter.charCodeAt(0) - note1.letter.charCodeAt(0);
          const isAdjacent = (diff === 1 || diff === -1) &&
            // Exclude B-C same-octave (only allow as cross-octave collision)
            !((note1.letter === 'b' && note2.letter === 'c') || (note1.letter === 'c' && note2.letter === 'b')) ||
            // Include G-A wraparound for same-octave  
            (note1.letter === 'a' && note2.letter === 'g') ||
            (note1.letter === 'g' && note2.letter === 'a');

          // Only allow B→C cross-octave collision when B is in lower octave than C
          const isCrossOctaveBC = octaveDiff === 1 && (
            (note1.letter === 'b' && note2.letter === 'c' && note2.octave === note1.octave + 1) ||
            (note1.letter === 'c' && note2.letter === 'b' && note1.octave === note2.octave + 1)
          );

          if (isAdjacent && octaveDiff === 0 || isCrossOctaveBC) {
            collisionCount++;
          }
        }
      }
    }

    // Adjust collision count to match VexFlow's actual collision behavior
    if (collisionCount <= 1) return collisionCount;
    return Math.ceil(collisionCount / 2);
  };

  const renderScale = (context: VF.RenderContext, stave: VF.Stave, voices: VF.Voice[], keySignature: string) => {
    const totalWidth = stave.getWidth();

    // Calculate extra space needed for key signature
    const keySignatureMap: { [key: string]: number } = {
      'C': 0,
      'G': 1, 'F': 1,
      'D': 2, 'Bb': 2,
      'A': 3, 'Eb': 3,
      'E': 4, 'Ab': 4,
      'B': 5, 'Db': 5,
      'F#': 6, 'Gb': 6,
      'C#': 7, 'Cb': 7
    };
    const numAccidentals = keySignatureMap[keySignature] || 0;

    // Calculate fixed width for each measure, accounting for the first measure's extra space needs
    const firstMeasureExtra = totalWidth * (0.08 + (numAccidentals * 0.04));  // Extra space for clef and key signature in first measure
    const availableWidth = totalWidth - firstMeasureExtra;
    const measureWidth = Math.floor(availableWidth / voices.length);

    // Create a measure for each note
    voices.forEach((voice, index) => {
      const note = voice.getTickables()[0] as VF.StaveNote;
      const hasAccidental = note.getModifiers().some((modifier: any) => modifier instanceof VF.Accidental);

      // Adjust measure width based on whether note has an accidental
      const adjustedMeasureWidth = hasAccidental ? measureWidth * 0.9 : measureWidth;
      const thisWidth = index === 0 ? adjustedMeasureWidth + firstMeasureExtra : adjustedMeasureWidth;
      const xPos = index === 0 ?
        stave.getX() :
        stave.getX() + firstMeasureExtra + (index * adjustedMeasureWidth);

      // Create a mini-stave for this note
      const miniStave = new VF.Stave(
        xPos,
        stave.getY(),
        thisWidth
      );

      // Don't draw bar lines
      miniStave.setEndBarType(VF.Barline.type.NONE);
      miniStave.setBegBarType(VF.Barline.type.NONE);

      // Only draw staff lines (no clef or key signature) except for first measure
      if (index === 0) {
        miniStave.addClef(stave.clef);
        if (keySignature !== 'C') {
          miniStave.addKeySignature(keySignature);
        }
      }

      miniStave.setContext(context).draw();

      // Format and draw the voice with consistent width
      new VF.Formatter()
        .joinVoices([voice])
        .format([voice], thisWidth * 0.1);  // Use 80% of measure width for the note

      voice.draw(context, miniStave);
    });
  };

  // Helper function to calculate key signature width based on existing accidentals function
  const getKeySignatureWidth = (keySignature: string): number => {
    if (keySignature === 'C') return 0;

    const accidentals = getKeySignatureAccidentals(keySignature);
    const accidentalCount = Object.keys(accidentals).length;

    // Sharp keys take slightly more horizontal space than flat keys
    const firstAccidental = Object.values(accidentals)[0];
    const isSharpKey = firstAccidental === '#';
    const baseWidth = isSharpKey ? 2.3 : 2.1; // Sharp keys are slightly wider

    return accidentalCount * baseWidth;
  };

  // Helper function to get Y position of highest note on the staff
  const getHighestNoteY = (highestNote: number, trebleStave: any, bassStave: any): number => {
    if (highestNote === -Infinity) return trebleStave.getYForLine(0); // Default to top of treble staff

    // Determine which staff the note would be on
    const usesTreble = highestNote >= 60; // Middle C and above typically go on treble
    const stave = usesTreble ? trebleStave : bassStave;

    // Convert MIDI note to VexFlow note position
    // This is a simplified calculation - VexFlow does more complex positioning
    const middleC = 60;
    const noteDiff = highestNote - middleC;

    if (usesTreble) {
      // Treble staff: middle C is below the staff
      const linePosition = 5 - (noteDiff * 0.5); // Approximate line position
      return stave.getYForLine(Math.max(0, Math.min(4, linePosition)));
    } else {
      // Bass staff: middle C is above the staff
      const linePosition = -1 - (noteDiff * 0.5); // Approximate line position
      return stave.getYForLine(Math.max(0, Math.min(4, linePosition + 6)));
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear the container
    containerRef.current.innerHTML = '';

    // Create VexFlow renderer
    const renderer = new VF.Renderer(containerRef.current, VF.Renderer.Backends.SVG);
    renderer.resize(pixelWidth, pixelHeight);
    const context = renderer.getContext();

    // Make SVG responsive by using percentage dimensions
    const svgElement = containerRef.current.querySelector('svg');
    if (svgElement) {
      svgElement.style.width = '100%';
      svgElement.style.height = '100%';
      svgElement.removeAttribute('width');
      svgElement.removeAttribute('height');
    }

    // Set white/black color for all elements
    // context.setFillStyle('black'); // light theme
    // context.setStrokeStyle('black'); // light theme
    context.setFillStyle(preferWhiteStaffPaper ? 'rgb(0, 0, 0)' : 'rgb(210, 210, 210)'); // dark theme
    context.setStrokeStyle(preferWhiteStaffPaper ? 'rgb(0, 0, 0)' : 'rgb(210, 210, 210)'); // dark theme

    // Scale the entire VexFlow context to make everything proportional
    context.scale(scale, scale);

    // Get key signature
    const keySignature = getKeySignature(lockedTonicNote, lockedTonicMode);

    // Create treble clef stave
    const trebleStave = new VF.Stave(10, 120, baseWidth - 20);
    trebleStave.addClef('treble');
    if (keySignature !== 'C') {
      trebleStave.addKeySignature(keySignature);
    }
    trebleStave.setContext(context).draw();

    // Create bass clef stave
    const bassStave = new VF.Stave(10, 215, baseWidth - 20);
    bassStave.addClef('bass');
    if (keySignature !== 'C') {
      bassStave.addKeySignature(keySignature);
    }
    bassStave.setContext(context).draw();

    // Create notes for each clef (needed for both chord and scale rendering)
    const trebleVoices = createVexFlowNotes(validMidiNotes, 'treble', keySignature, rootNote, mode, isScale);
    const bassVoices = createVexFlowNotes(validMidiNotes, 'bass', keySignature, rootNote, mode, isScale);

    // Calculate and create overtone notes (only if showOvertones is enabled)
    const allOvertoneNotes = showOvertones ? getOvertoneNotes(validMidiNotes) : [];
    // Filter out overtone notes that are already in the main notes
    const overtoneNotes = allOvertoneNotes.filter(overtoneNote => !validMidiNotes.includes(overtoneNote));
    const trebleOvertoneVoices = showOvertones ? createVexFlowNotes(overtoneNotes, 'treble', keySignature, rootNote, mode, isScale, true) : [];
    const bassOvertoneVoices = showOvertones ? createVexFlowNotes(overtoneNotes, 'bass', keySignature, rootNote, mode, isScale, true) : [];

    if (!isScale) {

      // Draw grand staff connections (brace and connecting line)
      // Connecting line on the left between staves
      context.beginPath();
      context.moveTo(10.5, trebleStave.getYForLine(4) + 0); // Bottom of treble staff
      context.lineTo(10.5, bassStave.getYForLine(0) - 0);   // Top of bass staff
      context.stroke();

      // Connecting line on the right between staves
      context.beginPath();
      context.moveTo(baseWidth - 9.5, trebleStave.getYForLine(4) + 0); // Bottom of treble staff
      context.lineTo(baseWidth - 9.5, bassStave.getYForLine(0) - 0);   // Top of bass staff  
      context.stroke();

      // Create notes for each clef (combining with overtones if showOvertones is enabled)
      const trebleNotes = createVexFlowNotes(validMidiNotes, 'treble', keySignature, rootNote, mode, isScale, false, showOvertones ? overtoneNotes : []);
      const bassNotes = createVexFlowNotes(validMidiNotes, 'bass', keySignature, rootNote, mode, isScale, false, showOvertones ? overtoneNotes : []);

      // Create voices and format together for proper alignment
      const voices: VoiceEntry[] = [];

      // Overtones are now combined with primary notes (above) for proper collision detection
      // No need to add separate overtone voices

      if (trebleNotes.length > 0) {
        const trebleVoice = new VF.Voice({ num_beats: 4, beat_value: 4 });
        trebleVoice.addTickables(trebleNotes);
        voices.push({ voice: trebleVoice, stave: trebleStave });
      }

      if (bassNotes.length > 0) {
        const bassVoice = new VF.Voice({ num_beats: 4, beat_value: 4 });
        bassVoice.addTickables(bassNotes);
        voices.push({ voice: bassVoice, stave: bassStave });
      }

      // Format all voices together to ensure alignment
      if (voices.length > 0) {
        const formatter = new VF.Formatter();
        voices.forEach(({ voice }) => formatter.joinVoices([voice]));

        // Calculate the minimum width needed for all voices
        const minWidth = Math.max(100, ...voices.map(({ voice }) =>
          formatter.preCalculateMinTotalWidth([voice])
        ));

        // Format with consistent width
        formatter.format(voices.map(({ voice }) => voice), baseWidth - 40);

        // Draw all voices
        voices.forEach(({ voice, stave }) => {
          voice.draw(context, stave);
        });
      }

    } else {
      // Render notes differently based on whether it's a scale or chord
      // if (trebleVoices.length > 0) renderScale(context, trebleStave, trebleVoices, keySignature);
      // if (bassVoices.length > 0) renderScale(context, bassStave, bassVoices, keySignature);
      const trebleNotes = createVexFlowNotes(validMidiNotes, 'treble', keySignature, rootNote, mode, true);
      const bassNotes = createVexFlowNotes(validMidiNotes, 'bass', keySignature, rootNote, mode, true);

      const maxBeats = Math.max(trebleNotes.length, bassNotes.length);

      const trebleVoice = new VF.Voice({ time: { num_beats: maxBeats, beat_value: 4 } });
      const bassVoice = new VF.Voice({ time: { num_beats: maxBeats, beat_value: 4 } });

      trebleVoice.setMode(VF.Voice.Mode.SOFT);
      bassVoice.setMode(VF.Voice.Mode.SOFT);

      trebleVoice.addTickables(trebleNotes);
      bassVoice.addTickables(bassNotes);

      // Pad shorter voice to align ticks
      const pad = (voice: any, count: number) => {
        for (let i = 0; i < count; i++) voice.addTickable(new VF.GhostNote({ duration: 'q' }));
      };
      pad(trebleVoice, maxBeats - trebleNotes.length);
      pad(bassVoice, maxBeats - bassNotes.length);

      const formatter = new VF.Formatter();

      // Only format and draw voices that have notes
      const voicesToFormat: any[] = [];
      if (trebleNotes.length > 0) {
        formatter.joinVoices([trebleVoice]);
        voicesToFormat.push(trebleVoice);
      }
      if (bassNotes.length > 0) {
        formatter.joinVoices([bassVoice]);
        voicesToFormat.push(bassVoice);
      }

      if (voicesToFormat.length > 0) {
        // Justify to the space that is actually left on the staff. Notes are drawn at
        // stave.getNoteStartX() + tick position, and getNoteStartX() grows with the clef
        // and key signature, so a fixed width (baseWidth - 70) ran keys with many
        // accidentals (E, C#, Gb, Ab...) off the right end of the staff.
        const noteStartX = Math.max(trebleStave.getNoteStartX(), bassStave.getNoteStartX());
        const noteEndX = Math.min(trebleStave.getNoteEndX(), bassStave.getNoteEndX());
        // Reserve only the left offset VexFlow adds to every note (Stave.padding, 12px);
        // the formatter already keeps a small margin of its own before the end of the
        // staff, so reserving the full defaultPadding just wasted 10px on the right.
        const noteLeftPadding = VF.Stave.defaultPadding - VF.Stave.rightPadding;
        const availableWidth = Math.max(60, noteEndX - noteStartX - noteLeftPadding);
        formatter.format(voicesToFormat, availableWidth);

        if (trebleNotes.length > 0) {
          trebleVoice.draw(context, trebleStave);
        }
        if (bassNotes.length > 0) {
          bassVoice.draw(context, bassStave);
        }
      }
    }


    // Note: The chord symbol rendering logic has been moved to a separate positioned div overlay
    // This allows proper React rendering of the formatted JSX elements

    // Scale down overtone noteheads via DOM manipulation (VexFlow has no public API for per-notehead scale)
    if (showOvertones && overtoneNotes.length > 0 && !isScale && containerRef.current) {
      const svgElement = containerRef.current.querySelector('svg');
      if (svgElement) {
        const noteheads = svgElement.querySelectorAll('.vf-notehead');
        const trebleCombined = [...new Set([...validMidiNotes, ...overtoneNotes])]
          .filter(note => shouldUseTrebleClef(note))
          .sort((a, b) => a - b);
        const bassCombined = [...new Set([...validMidiNotes, ...overtoneNotes])]
          .filter(note => !shouldUseTrebleClef(note))
          .sort((a, b) => a - b);
        const allRenderedNotes = [...trebleCombined, ...bassCombined];

        noteheads.forEach((notehead, index) => {
          const midiNote = allRenderedNotes[index];
          if (midiNote !== undefined && overtoneNotes.includes(midiNote)) {
            const el = notehead as SVGElement;
            // scaleY only — accidentals only affect horizontal bounds so y-center is always correct
            el.style.transformBox = 'fill-box';
            el.style.transformOrigin = 'center';
            el.style.transform = 'scale(1, 0.9) translateY(0.1px)';
            el.classList.add('overtone-pulse-staff');
          }
        });
      }
    }

  }, [validMidiNotes, keyCenter, mode, width, height, lockedTonicNote, lockedTonicMode, rootNote, isScale, chordSymbols, chordSymbol, csOptionOneSegments, scaleRoot, activeNotes, heldNotes, toggledNotes, assembleChordSymbol, assembleScaleName, scaleUrl, activePcs, windowSize, showOvertones, appTheme]);

  // Separate useEffect for flashing to avoid multiple triggers
  useEffect(() => {
    if (keyflash && keyflash.length > 0 && containerRef.current) {
      setTimeout(() => {
        const svgElement = containerRef.current && containerRef.current.querySelector('svg');
        if (svgElement) {
          const noteheads = svgElement.querySelectorAll('.vf-notehead');

          // Calculate overtone notes for this flash cycle
          const allOvertoneNotes = showOvertones ? getOvertoneNotes(validMidiNotes) : [];
          const overtoneNotes = allOvertoneNotes.filter(overtoneNote => !validMidiNotes.includes(overtoneNote));

          // We need to match rendered noteheads to MIDI notes
          // NOW: overtones and primary notes are combined in a single chord, sorted by pitch
          // Build the complete list in NEW rendering order (combined and sorted)
          const trebleCombined = [...new Set([...validMidiNotes, ...(showOvertones ? overtoneNotes : [])])]
            .filter(note => shouldUseTrebleClef(note))
            .sort((a, b) => a - b);
          const bassCombined = [...new Set([...validMidiNotes, ...(showOvertones ? overtoneNotes : [])])]
            .filter(note => !shouldUseTrebleClef(note))
            .sort((a, b) => a - b);

          // Combine in rendering order: treble combined, then bass combined
          const allRenderedNotes = [...trebleCombined, ...bassCombined];

          // Count notes in each clef for the multiple notes check
          const trebleNotes = validMidiNotes.filter(note => shouldUseTrebleClef(note));
          const bassNotes = validMidiNotes.filter(note => !shouldUseTrebleClef(note));

          // Calculate overtones for held notes to include in restrike check
          const validNotesWithOvertones = [...validMidiNotes, ...allOvertoneNotes];

          noteheads.forEach((notehead, index) => {
            const correspondingMidiNote = allRenderedNotes[index];
            if (correspondingMidiNote) {
              // Check if this note should flash - flash if it's a restrike of any sustaining note (primary or overtone)
              const isRestrike = keyflash.includes(correspondingMidiNote) && validNotesWithOvertones.includes(correspondingMidiNote);
              const isOvertoneOfRestrike = showOvertones && keyflash.some(flashNote => {
                // Check if this overtone is produced by a flashing main note that is being restruck
                if (heldNotes.includes(flashNote)) {
                  const flashNoteOvertones = getOvertoneNotes([flashNote]);
                  return flashNoteOvertones.includes(correspondingMidiNote);
                }
                return false;
              });

              const shouldFlash = isRestrike || isOvertoneOfRestrike;

              if (shouldFlash) {
                // Only flash if there are multiple notes in that clef (for main notes) or always flash overtones
                const isInTrebleClef = shouldUseTrebleClef(correspondingMidiNote);
                const noteCountInClef = isInTrebleClef ? trebleNotes.length : bassNotes.length;
                const isOvertone = overtoneNotes.includes(correspondingMidiNote);

                if (noteCountInClef > 0 || isOvertone) {
                  const svgNotehead = notehead as SVGElement;

                  // Flash sequence using direct style manipulation
                  svgNotehead.style.fill = preferWhiteStaffPaper ? 'rgb(255, 255, 255)' : 'rgb(0, 0, 0)';

                  // Return to original color after flash duration
                  // setTimeout(() => {
                  //   svgNotehead.style.fill = isOvertone ? 'rgb(128, 128, 128)' : 'black';
                  // }, 400);
                }
              }
            }
          });
        }
      }, 5); // Small delay to ensure rendering is complete
    }
  }, [keyflash, validMidiNotes, showOvertones, heldNotes]); // Only depend on keyflash and necessary data

  return (
    <div style={{ position: 'relative' }}>
      {/* StaffView SVG Container */}
      <div
        ref={containerRef}
        style={{
          // backgroundColor: 'rgba(255, 255, 255, 0.75)', // light theme
          backgroundColor: preferWhiteStaffPaper ?
            `rgba(255, 255, 255, ${appTheme === "light" ? 0.65 : 0.85})` : // white paper on light theme vs dark and warm theme
            `rgba(0, 0, 0, ${appTheme === "light" ? 0.75 : appTheme === "dark" ? 0.7 : 0.85})`, // dark theme and warm theme
          borderRadius: '1dvw',
          // border: 'solid 0.05dvw rgba(125, 125, 125, 1.0)', // light theme
          border: preferWhiteStaffPaper ? 'solid 0.05dvw rgba(125, 125, 125, 0.7)' : 'solid 0.05dvw rgba(125, 125, 125, 0.7)', // dark theme
          padding: '0.0dvw',
          boxShadow: `0 0 0.6dvw 0.35dvw rgba(0, 0, 0, ${appTheme === "light" ? 0.2 : 0.5}), inset 0dvw 0dvw 2dvw 0.49dvw rgba(10, 10, 0,
          ${appTheme === "light" ? preferWhiteStaffPaper ? 0.0 : 0.7 : preferWhiteStaffPaper ? 0.7 : 0.0})`,
          width: width,
          height: height
        }}
      />

      {/* Chord Symbol Overlay */}
      {chordSymbols && chordSymbols.split('\n')[0] && (
        <div className="chord-display" style={{
          position: 'absolute',
          fontFamily: 'Bravura Text', // Explicitly set font for inheritance
          left: (() => {
            if (!isScale) {
              // Calculate horizontal position with key signature offset
              const keySignature = getKeySignature(lockedTonicNote, lockedTonicMode);
              const keySignatureWidth = getKeySignatureWidth(keySignature);

              // Get actual horizontal extent of rendered notes and accidentals
              const svgElement = containerRef.current && containerRef.current.querySelector('svg');
              let visualCenterX = (6 + keySignatureWidth) * 1; // Default fallback

              if (svgElement) {
                // Find all rendered accidentals and noteheads
                const accidentals = svgElement.querySelectorAll('.vf-accidental');
                const noteheads = svgElement.querySelectorAll('.vf-notehead');

                let leftmostX = Infinity;
                let rightmostX = -Infinity;

                // Get the bounding box of all accidentals (left side)
                accidentals.forEach((accidental: Element) => {
                  const bbox = (accidental as SVGGraphicsElement).getBBox();
                  leftmostX = Math.min(leftmostX, bbox.x);
                  rightmostX = Math.max(rightmostX, bbox.x + bbox.width);
                });

                // Get the bounding box of all noteheads (right side)
                noteheads.forEach((notehead: Element) => {
                  const bbox = (notehead as SVGGraphicsElement).getBBox();
                  leftmostX = Math.min(leftmostX, bbox.x);
                  rightmostX = Math.max(rightmostX, bbox.x + bbox.width);
                });

                // If we found rendered elements, calculate visual center
                if (leftmostX !== Infinity && rightmostX !== -Infinity) {
                  const collisionCount = detectCollisions(validMidiNotes);
                  const collisionOffset = collisionCount > 0 ? Math.max(3, 9 - collisionCount * 2) : 0; // More collisions = less shift
                  const visualCenterPx = (leftmostX + rightmostX) * 0.45 - keySignatureWidth - collisionOffset;
                  visualCenterX = visualCenterPx * 0.1; // Convert pixels to dvw
                }
              }

              // Simple centering adjustment based on chord length
              const chordLength = chordSymbol.length || chordSymbols.split('\n')[0].length;
              const chordCenteringOffset = -chordLength * 0.2 - 3.0; // Longer chords shift slightly left, 3.4 fine tuning adjustment

              return `${visualCenterX + chordCenteringOffset}dvw`;
            } else {
              return '2.0dvw'; // Scale position from left
            }
          })(),
          top: (() => {
            const overtones = showOvertones ? getOvertoneNotes([...activeNotes, ...heldNotes, ...toggledNotes]) : [];
            // Calculate top position based on highest MIDI note
            const highestNote = Math.max(
              ...activeNotes,
              ...heldNotes,
              ...toggledNotes,
              ...overtones
            );
            // Scale position from bottom to top based on note height
            const minNote = 75;
            const maxNote = 108;
            const bottomPos = 7.1;  // Lowest position inside StaffView (dvw)
            const topPos = 0;      // Highest position inside StaffView (dvw)

            if (!isScale) {
              if (highestNote <= minNote) return `${bottomPos}dvw`;
              if (highestNote >= maxNote) return `${topPos}dvw`;

              // Linear interpolation between bottom and top positions
              const percentage = (highestNote - minNote) / (maxNote - minNote);
              return `${bottomPos - (percentage * (bottomPos - topPos))}dvw`;
            } else {
              return '5.9dvw'; // Scale name vertical position
            }
          })(),
          zIndex: 2,
          pointerEvents: 'none' // Allow clicks to pass through
        }}>
          {isScale && assembleScaleName ? (
            <a style={{
              // color: "black", // light theme
              color: preferWhiteStaffPaper ? "rgb(0, 0, 0)" : "rgb(255, 255, 255)", // dark theme
              textDecoration: "none",
              fontFamily: 'Bravura Text',
              fontSize: `${2.1 - (chordSymbol.length * 0.03)}dvw`,
              pointerEvents: 'auto' // Enable clicks on the link  
            }}
              href={scaleUrl}
              target="_blank"
            // rel="noopener noreferrer"
            >
              {assembleScaleName(scaleRoot, mode)}
            </a>
          ) : (
            csOptionOneSegments && assembleChordSymbol ? (
              <span
                className={activePcs.length === 13 ? "note" : "chord"}
                style={{
                  fontSize: `${2.2 - (chordSymbol.length * 0.01)}dvw`,
                  // color: 'black', // light theme
                  color: preferWhiteStaffPaper ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)', // dark theme
                  fontFamily: 'inherit'
                }}
              >
                {assembleChordSymbol(csOptionOneSegments, false, lockedTonicNote, lockedTonicMode)}
              </span>
            ) : (
              <span
                className={activePcs.length === 13 ? "note" : "chord"}
                style={{
                  fontSize: `${2.2 - (chordSymbol.length * 0.01)}dvw`,
                  color: 'black',
                  fontFamily: 'inherit'
                }}
              >
                {chordSymbols.split('\n')[0]}
              </span>
            )
          )}
        </div>
      )}

      {/* Roman Numeral Overlay */}
      {!isScale && showRomanNumerals && lockedTonicNote !== null && chordSymbols && chordSymbols.split('\n')[0] && (
        <div className="roman-numeral-display" style={{
          position: 'absolute',
          fontFamily: 'Bravura Text',
          left: (() => {
            if (!isScale) {
              // Calculate horizontal position with key signature offset
              const keySignature = getKeySignature(lockedTonicNote, lockedTonicMode);
              const keySignatureWidth = getKeySignatureWidth(keySignature);

              // Get actual horizontal extent of rendered notes and accidentals
              const svgElement = containerRef.current && containerRef.current.querySelector('svg');
              let visualCenterX = (6 + keySignatureWidth) * 1; // Default fallback

              if (svgElement) {
                // Find all rendered accidentals and noteheads
                const accidentals = svgElement.querySelectorAll('.vf-accidental');
                const noteheads = svgElement.querySelectorAll('.vf-notehead');

                let leftmostX = Infinity;
                let rightmostX = -Infinity;

                // Get the bounding box of all accidentals (left side)
                accidentals.forEach((accidental: Element) => {
                  const bbox = (accidental as SVGGraphicsElement).getBBox();
                  leftmostX = Math.min(leftmostX, bbox.x);
                  rightmostX = Math.max(rightmostX, bbox.x + bbox.width);
                });

                // Get the bounding box of all noteheads (right side)
                const accidentalOffset = accidentals.length * 18; // 8px per accidental
                noteheads.forEach((notehead: Element) => {
                  const bbox = (notehead as SVGGraphicsElement).getBBox();
                  leftmostX = Math.min(leftmostX, bbox.x + accidentalOffset);
                  rightmostX = Math.max(rightmostX, bbox.x + bbox.width);
                });

                // If we found rendered elements, calculate visual center
                if (leftmostX !== Infinity && rightmostX !== -Infinity) {
                  const collisionCount = detectCollisions(validMidiNotes);
                  const collisionOffset = collisionCount > 0 ? Math.max(3, 9 - collisionCount * 2) : 0; // More collisions = less shift
                  const visualCenterPx = (leftmostX + rightmostX) * 0.45 - keySignatureWidth - collisionOffset;
                  visualCenterX = visualCenterPx * 0.1; // Convert pixels to dvw
                }
              }

              // Get the chord symbol's root pitch class for Roman numeral calculation  
              const chordRootPc = csOptionOneSegments && csOptionOneSegments.root_pc !== undefined
                ? csOptionOneSegments.root_pc
                : rootNote % 12;
              const romanNumeral = showNashvilleNumber ?
                midiToNashvilleNumber(chordRootPc, lockedTonicNote!, lockedTonicMode!, isScale ? mode : chordQual) :
                midiToRomanNumeral(chordRootPc, lockedTonicNote!, lockedTonicMode!, isScale ? mode : chordQual);
              const romanLength = romanNumeral ? romanNumeral.length : 5;
              const romanCenteringOffset = -romanLength * 0.3 - 1.0; // Adjust centering based on length

              return `${visualCenterX + romanCenteringOffset}dvw`;
            } else {
              return '2.4dvw';
            }
          })(),
          top: (() => {
            // Calculate bottom position based on lowest MIDI note (inverted from chord symbols)
            const lowestNote = Math.min(
              ...activeNotes,
              ...heldNotes,
              ...toggledNotes
            );

            // Position below bass staff, moving down for lower notes
            const maxNote = 45;  // Start moving down from this note
            const minNote = 21;  // Lowest possible note
            const topPos = 24.0;   // Highest position below bass staff (dvw)
            const bottomPos = 28.9; // Lowest position below bass staff (dvw)

            if (lowestNote >= maxNote) return `${topPos}dvw`;
            if (lowestNote <= minNote) return `${bottomPos}dvw`;

            // Linear interpolation between top and bottom positions
            const percentage = (maxNote - lowestNote) / (maxNote - minNote);
            return !isScale ? `${topPos + (percentage * (bottomPos - topPos))}dvw` : `${topPos}dvw`;
          })(),
          zIndex: 2,
          pointerEvents: 'none' // Allow clicks to pass through
        }}>
          {(() => {
            // Get chord root from chord segments, fallback to rootNote
            const chordRootPc = csOptionOneSegments && csOptionOneSegments.root_pc !== undefined
              ? csOptionOneSegments.root_pc
              : rootNote % 12;
            const romanNumeral = showNashvilleNumber ?
              midiToNashvilleNumber(chordRootPc, lockedTonicNote!, lockedTonicMode!, isScale ? mode : chordQual) :
              midiToRomanNumeral(chordRootPc, lockedTonicNote!, lockedTonicMode!, isScale ? mode : chordQual);

            // Font size adjustment based on mode
            const romanLength = romanNumeral ? romanNumeral.length : 5;
            const fontAdjust = 0.0 + ((5 - romanLength) / 4) * (0.3 - 0.0);
            const fontSize = (mode === 1 || showNashvilleNumber)
              ? (2.0 - fontAdjust) // Minor mode - larger font
              : (1.8 - fontAdjust); // Major mode

            return (
              <span style={{
                fontSize: `${fontSize}dvw`,
                // color: 'rgb(90, 90, 90)', // light theme
                color: preferWhiteStaffPaper ? 'rgb(90, 90, 90)' : 'rgb(195, 195, 195)', // dark theme
                // background: 'rgba(255, 255, 255, 0.5)', // light theme
                // background: 'rgba(70, 70, 70, 0.5)', // dark theme
                background: preferWhiteStaffPaper ? mode === 1 ? 'rgba(100, 160, 255, 0.6)' : 'rgba(214, 214, 48, 0.6)' : mode === 1 ? 'rgb(30, 60, 122)' : 'rgb(90, 90, 0)', // dark theme
                padding: '0.4dvw 0.4dvw',
                borderRadius: '0.4dvw',
                // boxShadow: '0.0dvw 0.1dvw 0.2dvw 0.2dvw rgb(0, 0, 0, 0.15)',
              }}>
                {romanNumeral}
                {/* {!isScale && chordQual === 1 && <span style={{ fontSize: '0.8em' }}></span>} */}
                {!isScale && chordQual === 2 || chordQual === 5 && <sup style={{ fontSize: '0.8em' }}>7</sup>}
                {!isScale && chordQual === 3 && <span>°</span>}
                {!isScale && chordQual === 1 && showNashvilleNumber && <span>m</span>}
                {!isScale && chordQual === 4 && <sup style={{ fontSize: '0.8em' }}>+</sup>}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default StaffView;